import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import type { ReactElement } from "react";
import { authenticatedApi } from "@/lib/api";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { RdThreadChat } from "@/components/v2/rd-thread-chat";
import type { SessionRowData } from "@/components/v2/session-row";
import { useRdDetail, type RdDetailData } from "@/lib/use-rd-detail";
import { isReadSourceType, type ReadSourceRef } from "../../../shared/chat-sources";
import type { FormSnapshotData } from "../../../shared/form-types";
import type { ObservationNoteData } from "../../../shared/observation-types";
import type { MeetingNoteResponse } from "../../../shared/api-types";
import { formatLocalDate } from "../../../shared/local-date";
import type {
  WorkspaceSummaryResponse,
  WorkspaceThreadResponse,
} from "../../../shared/api-types";
import {
  STARTER_PROMPT_GROUPS,
  type StarterPromptLibraryData,
} from "../../../shared/starter-prompts";

function SurfaceMessage({ text }: { text: string }): ReactElement {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
}

/**
 * New chat launcher: freeform composer over the RD manifest. The thread row is
 * created on first send, so abandoning this page leaves nothing behind.
 * `?sourceType=&sourceId=` identifies an optional focus source. Source content
 * remains pull-based and is never copied into the URL or composer.
 */
export function ChatsNewPage(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const search = useSearch({ strict: false }) as {
    sourceType?: unknown;
    sourceId?: unknown;
  };
  const requestedSource = requestedChatSource(search);
  const { getToken } = useAuth();
  const { rd, sessions, summary, starterPrompts, focusedRecord } = useNewChatQueries(
    rdId,
    getToken,
    requestedSource
  );
  const handleFirstTurnPersisted = useFirstTurnPersisted(rdId, getToken);

  const detail = rd.data?.data;

  if (rd.isError) {
    return <ChatQueryError query={rd} notFoundText="RD not found" />;
  }

  if (sessions.isError) {
    return <ChatQueryError query={sessions} notFoundText="Something went wrong" />;
  }

  if (summary.isError) {
    return <ChatQueryError query={summary} notFoundText="Something went wrong" />;
  }

  const missingChatData = !detail || !sessions.data || !summary.data;

  if (missingChatData || starterPrompts.isPending) {
    return <SurfaceMessage text="Loading..." />;
  }

  // Starter prompts are an enhancement, not a dependency: if the library read
  // fails, degrade to the code-owned defaults instead of blocking New chat.
  const starterPromptGroups = starterPrompts.data?.data.groups ?? [
    ...STARTER_PROMPT_GROUPS,
  ];

  const sessionRows = sessions.data.data;
  const source = availableChatSource(requestedSource, sessionRows);
  const sourceLabel = chatSourceLabel(
    source,
    sessionRows,
    focusedRecord.data?.data ?? null
  );

  return (
    <NewChatContent
      detail={detail}
      sessionRows={sessionRows}
      source={source}
      sourceLabel={sourceLabel}
      summary={summary.data.data}
      starterPromptGroups={starterPromptGroups}
      onFirstTurnPersisted={handleFirstTurnPersisted}
    />
  );
}

function NewChatContent({
  detail,
  sessionRows,
  source,
  sourceLabel,
  summary,
  starterPromptGroups,
  onFirstTurnPersisted,
}: {
  detail: RdDetailData;
  sessionRows: SessionRowData[];
  source: ReadSourceRef | null;
  sourceLabel: string | null;
  summary: WorkspaceSummaryResponse;
  starterPromptGroups: StarterPromptLibraryData["groups"];
  onFirstTurnPersisted: (chatId: string) => Promise<void>;
}): ReactElement {
  return (
    <div className="h-full">
      <BreadcrumbSetter
        segments={[
          { label: "RDs", href: "/rds" },
          { label: detail.name, href: `/rds/${detail.slug}` },
          { label: "New chat" },
        ]}
      />
      <RdThreadChat
        rdId={detail.id}
        rdSlug={detail.slug}
        rdName={detail.name}
        chatId={null}
        threadTitle={null}
        threadKind="adhoc"
        initialMessages={[]}
        sessions={sessionRows}
        readOnly={false}
        source={source}
        sourceLabel={sourceLabel}
        onFirstTurnPersisted={onFirstTurnPersisted}
        recordCounts={{
          sessions: sessionRows.length,
          meetings: summary.meetingNoteCount,
          observations: summary.observationCount,
        }}
        starterPromptGroups={starterPromptGroups}
      />
    </div>
  );
}

type GetToken = ReturnType<typeof useAuth>["getToken"];
type FocusedRecord = FormSnapshotData | ObservationNoteData | MeetingNoteResponse;

function requestedChatSource(search: {
  sourceType?: unknown;
  sourceId?: unknown;
}): ReadSourceRef | null {
  if (!isReadSourceType(search.sourceType)) return null;

  if (typeof search.sourceId !== "string" || !search.sourceId) return null;

  return { type: search.sourceType, id: search.sourceId };
}

function useNewChatQueries(
  rdId: string,
  getToken: GetToken,
  requestedSource: ReadSourceRef | null
) {
  const rd = useRdDetail(rdId);
  const sessions = useQuery({
    queryKey: ["rd-sessions", rdId],
    queryFn: () =>
      authenticatedApi<{ data: SessionRowData[] }>(
        getToken,
        `/api/rds/${rdId}/sessions`
      ),
  });
  const summary = useQuery({
    queryKey: ["workspace-summary", rdId],
    queryFn: () =>
      authenticatedApi<{ data: WorkspaceSummaryResponse }>(
        getToken,
        `/api/rds/${rdId}/chats`
      ),
  });
  const starterPrompts = useQuery({
    queryKey: ["starter-prompts"],
    queryFn: () =>
      authenticatedApi<{ data: StarterPromptLibraryData }>(
        getToken,
        "/api/starter-prompts"
      ),
  });
  const focusedRecord = useQuery({
    queryKey: ["focused-chat-source", rdId, requestedSource?.type, requestedSource?.id],
    queryFn: async () => {
      if (!requestedSource || requestedSource.type === "session") return null;

      const path =
        requestedSource.type === "form"
          ? `/api/rds/${rdId}/forms/${requestedSource.id}`
          : requestedSource.type === "observation"
            ? `/api/rds/${rdId}/observations/${requestedSource.id}`
            : `/api/rds/${rdId}/meeting-notes/${requestedSource.id}`;

      return authenticatedApi<{
        data: FormSnapshotData | ObservationNoteData | MeetingNoteResponse;
      }>(getToken, path);
    },
    enabled: requestedSource !== null && requestedSource.type !== "session",
    retry: false,
  });

  return { rd, sessions, summary, starterPrompts, focusedRecord };
}

function useFirstTurnPersisted(rdId: string, getToken: GetToken) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // First turn persisted: swap the launcher for the thread route in place.
  // Prefetching first means the thread page renders the identical DB-backed
  // turn straight from cache — no loading flash mid-conversation. Prefetch
  // failures are swallowed by react-query; the thread page then just fetches
  // on mount.
  return useCallback(
    async (chatId: string) => {
      await queryClient.prefetchQuery({
        queryKey: ["thread", rdId, chatId],
        queryFn: () =>
          authenticatedApi<{ data: WorkspaceThreadResponse }>(
            getToken,
            `/api/rds/${rdId}/chats/${chatId}/messages`
          ),
        retry: false,
      });
      void navigate({
        to: "/rds/$rdId/chats/$chatId",
        params: { rdId, chatId },
        replace: true,
      });
    },
    [queryClient, navigate, getToken, rdId]
  );
}

function availableChatSource(
  requestedSource: ReadSourceRef | null,
  sessions: SessionRowData[]
): ReadSourceRef | null {
  if (requestedSource?.type === "session") {
    return sessions.some((session) => session.id === requestedSource.id)
      ? requestedSource
      : null;
  }

  return requestedSource;
}

function chatSourceLabel(
  source: ReadSourceRef | null,
  sessions: SessionRowData[],
  record: FocusedRecord | null
): string | null {
  if (!source) return null;

  if (source.type !== "session") return focusedSourceLabel(source, record);

  const session = sessions.find((row) => row.id === source.id);
  const date = formatLocalDate(session?.sessionDate ?? null) || "Undated";

  return `${date} scorecard${session?.clientName ? ` · ${session.clientName}` : ""}`;
}

function focusedSourceLabel(
  source: ReadSourceRef,
  record: FormSnapshotData | ObservationNoteData | MeetingNoteResponse | null
): string {
  if (source.type === "form") {
    const form = record as FormSnapshotData | null;
    if (!form) return "Functional form";

    const kind = form.formType === "weekly_form" ? "weekly form" : "monthly checkpoint";

    return `Week of ${formatLocalDate(form.periodStartDate)} ${kind}`;
  }

  if (source.type === "observation") {
    const observation = record as ObservationNoteData | null;

    return (
      observation?.title?.trim() ||
      `${formatLocalDate(observation?.observationDate ?? null) || "Undated"} observation`
    );
  }

  if (source.type === "meeting_note") {
    const meeting = record as MeetingNoteResponse | null;

    return (
      meeting?.title?.trim() ||
      `${formatLocalDate(meeting?.meetingDate ?? null) || "Undated"} meeting note`
    );
  }

  return "Session scorecard";
}

/**
 * One chat thread. Owner-scoped: the owner chats; an admin gets a read-only
 * view; anyone else sees 404 — thread existence is never leaked across
 * supervisors (enforced server-side by GET /chats/:chatId/messages).
 */
export function ChatThreadPage(): ReactElement {
  const { rdId, chatId } = useParams({ strict: false }) as {
    rdId: string;
    chatId: string;
  };
  const { getToken } = useAuth();
  const rd = useRdDetail(rdId);
  const sessions = useQuery({
    queryKey: ["rd-sessions", rdId],
    queryFn: () =>
      authenticatedApi<{ data: SessionRowData[] }>(
        getToken,
        `/api/rds/${rdId}/sessions`
      ),
  });
  const thread = useQuery({
    queryKey: ["thread", rdId, chatId],
    queryFn: () =>
      authenticatedApi<{ data: WorkspaceThreadResponse }>(
        getToken,
        `/api/rds/${rdId}/chats/${chatId}/messages`
      ),
    retry: false,
  });

  const detail = rd.data?.data;

  if (thread.isError) {
    return <ChatQueryError query={thread} notFoundText="Thread not found" />;
  }

  if (rd.isError) {
    return <ChatQueryError query={rd} notFoundText="RD not found" />;
  }

  if (sessions.isError) {
    return <ChatQueryError query={sessions} notFoundText="Something went wrong" />;
  }

  if (!detail || !sessions.data || !thread.data)
    return <SurfaceMessage text="Loading..." />;

  return (
    <ThreadContent
      detail={detail}
      chat={thread.data.data}
      sessions={sessions.data.data}
    />
  );
}

function ThreadContent({
  detail,
  chat,
  sessions,
}: {
  detail: RdDetailData;
  chat: WorkspaceThreadResponse;
  sessions: SessionRowData[];
}): ReactElement {
  return (
    <div className="h-full">
      <BreadcrumbSetter
        segments={[
          { label: "RDs", href: "/rds" },
          { label: detail.name, href: `/rds/${detail.slug}` },
          { label: chat.title?.trim() || "Chat" },
        ]}
      />
      <RdThreadChat
        key={chat.chatId}
        rdId={detail.id}
        rdSlug={detail.slug}
        rdName={detail.name}
        chatId={chat.chatId}
        threadTitle={chat.title}
        threadKind={chat.kind}
        threadCreatedAt={chat.createdAt}
        initialMessages={chat.messages}
        sessions={sessions}
        readOnly={chat.ownerName !== null}
        ownerName={chat.ownerName}
      />
    </div>
  );
}

function ChatQueryError({
  query,
  notFoundText,
}: {
  query: { error: unknown; refetch: () => Promise<unknown> };
  notFoundText: string;
}): ReactElement {
  return (
    <QueryErrorState
      error={query.error}
      notFoundText={notFoundText}
      onRetry={() => void query.refetch()}
      className="h-full"
    />
  );
}
